import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { Stack } from '@mui/material'
import { AttributeName, IGenericVehicle, IVehicleAttribute } from '@cbgms/api/modules/vehicles'
import { useArrowScrollHandler } from '@carsys/ui/src/helpers/keyboard/use-arrow-scroll-handler'
import { useOnEnterHandler } from '@carsys/ui/src/helpers/keyboard/use-on-enter-handler'
import { ITypeRowItem, useTypeFilter } from 'app/vehicles/components/UnknownVehicleV2/hooks/use-type-filter'
import {
  DataGridPro,
  GridColDef,
  GridRowParams,
  GridValueGetterParams,
  getGridSingleSelectOperators,
  useGridApiRef
} from '@mui/x-data-grid-pro'
import { useDataGridLocaleText } from '@cbgms/components/list/use-data-grid-locale-text'
import {
  buildYearDisplayValue,
  buildYearRange,
  generateUniqueOptions,
  getRowByIndex,
  getVisibleRowCount,
  onKeyScroll,
  parseVehicleDate,
  sortFilterOptions
} from '../../grid/Helpers'
import { DataGridToolbarWithSearch } from '../../grid/GridToolBarWithSearch'
import { GridHeaderFilterSingleSelect } from '../../grid/GridHeaderFilterSingleSelect'
import { gridHeaderStyling } from '../../grid/Styling'
import { DisplayType, MDMSource, SortDirection, VehicleSource } from '@cbgms/api'
import { GridDateRangeIncludes } from '../../grid/GridDateRangeIncludes'
import { usePrimaryVehicleSource } from 'app/vehicles/helpers/use-primary-vehicle-source'

interface ITypeSelectionProps {
  onTypeSelect: (type: IGenericVehicle) => void
  types: IGenericVehicle[]
  hideSearch?: boolean
  selectedKey?: string
}

interface IAttributeKeyValues {
  [key: string]: string
}

interface IHeaderItem {
  name: string
  displayName: string
  displayType: string
  containsDifferentValues: boolean
  values: string[]
  sort: number
}

const columnSortOrder = [
  'Type', // // 'TecDocPlus' attribute
  'ModelVersionName', // 'EMDM' attribute
  'Description', // 'EMDM' attribute
  'BuildYear', // our custom formatted attribute
  'EngineSize', // 'EMDM' attribute
  'Fuel', // 'EMDM' attribute
  'EngineCode', // 'EMDM' attribute
  'kW', // 'EMDM' attribute
  'CC' // 'EMDM' attribute
]
const columnBlacklist = [
  'EntityInstanceID', // 'EMDM' attribute
  'MMTSelector', // 'EMDM' attribute
  'SatorVehicleTypeConsol', // 'EMDM' attribute
  'VehicleID', // 'EMDM' attribute
  'EUTypeApproval', // 'EMDM' attribute
  'VehicleYear', // 'EMDM' attribute
  'EdidomusConsolVehicleID', // 'EMDM' attribute
  'HaynesProVehicleID', // 'EMDM' attribute
  'KTypeID', // 'EMDM' attribute
  AttributeName.EMDMDateFrom,
  AttributeName.EMDMDateTo,
  'TecDocTypeNo', // 'TecDocPlus' attribute
  AttributeName.TecDocPlusDateFrom,
  AttributeName.TecDocPlusDateTo
]

const columnsWithUniqueValues = (attributes: IVehicleAttribute[]) => {
  const output: { [key: string]: IVehicleAttribute[] | undefined } = {}
  for (let i = 0; i < attributes.length; i++) {
    // check if this was already assigned
    if (output[attributes[i].Name] === undefined) {
      // assign the first value
      output[attributes[i].Name] = [attributes[i]]
    } else {
      // this already was assigned, it means a duplicate attribute was provided, which means this value is for user selection
      output[attributes[i].Name]!.push(attributes[i])
    }
  }
  return output
}

const addCustomBuildYearColumn = (t: TFunction, attributes: IVehicleAttribute[]) => {
  const dateFrom = findAttributeDateFrom(attributes)
  const dateTo = findAttributeDateTo(attributes)
  const displayValue = buildYearDisplayValue(t, dateFrom?.Value, dateTo?.Value)
  const customBuildYearAttr: IVehicleAttribute = {
    Name: 'BuildYear',
    Value: displayValue,
    Source: MDMSource.Unknown,
    DisplayName: 'BuildYear',
    DisplayValue: displayValue,
    DisplayType: DisplayType.Info,
    ResetOrder: 0,
    SortOrder: 0
  }
  return { BuildYear: [customBuildYearAttr] }
}

function generateBuildYearOptions(rows: ITypeRowItem[], primaryVehicleSource: VehicleSource) {
  const result = rows.reduce(
    (result, row) => {
      const dateFrom = findAttributeDateFrom(resolveAttributes(row.value, primaryVehicleSource))
      const dateUnit = findAttributeDateFrom(resolveAttributes(row.value, primaryVehicleSource))
      const madeFrom = parseVehicleDate(dateFrom?.Value).year()
      const madeUnit = parseVehicleDate(dateUnit?.Value).year()
      if (result.from > madeFrom) {
        result.from = madeFrom
      }
      if (result.until < madeUnit) {
        result.until = madeUnit
      }
      return result
    },
    { from: new Date().getFullYear(), until: 0 }
  )
  const buildYearRanges = buildYearRange(result.from, result.until)
  const uniqueOptions = generateUniqueOptions(buildYearRanges)
  const sortedOptions = sortFilterOptions(uniqueOptions, SortDirection.Descending)
  return { options: sortedOptions }
}

const findAttributeDateFrom = (attributes: IVehicleAttribute[] | undefined) => {
  return (attributes || []).find(
    attribute => attribute.Name === AttributeName.EMDMDateFrom || attribute.Name === AttributeName.TecDocPlusDateFrom
  )
}
const findAttributeDateTo = (attributes: IVehicleAttribute[] | undefined) => {
  return (attributes || []).find(
    attribute => attribute.Name === AttributeName.EMDMDateTo || attribute.Name === AttributeName.TecDocPlusDateTo
  )
}

const getRowDateFrom = (value: { row: undefined | IAttributeKeyValues }) => {
  return value.row?.[AttributeName.EMDMDateFrom] || value.row?.[AttributeName.TecDocPlusDateFrom]
}

const getRowDateTo = (value: { row: undefined | IAttributeKeyValues }) => {
  return value.row?.[AttributeName.EMDMDateTo] || value.row?.[AttributeName.TecDocPlusDateTo]
}

const resolveAttributes = (v: IGenericVehicle, primaryVehicleSource: VehicleSource): IVehicleAttribute[] => {
  if (primaryVehicleSource === VehicleSource.VPOM_Hbase && v.VPOMHbaseAttributes?.length) {
    return v.VPOMHbaseAttributes
  } else if (v.TecDocPlusAttributes?.length) {
    return v.TecDocPlusAttributes
  }
  return v.MDMAttributes || []
}

export const VPOMTypeList: React.FC<ITypeSelectionProps> = ({ types, onTypeSelect }) => {
  const { t } = useTranslation()
  const localeText = useDataGridLocaleText()
  const apiRef = useGridApiRef()
  const { source } = usePrimaryVehicleSource()

  const { typesAsRows, headerCells } = useMemo(() => {
    // output a list of attribute names + keys, and whether to show those.
    // output a list of all type rows, + column values.
    const typesAsRows = types.map<ITypeRowItem>((typeRow, i) => ({
      value: typeRow,
      columns: {
        ...columnsWithUniqueValues(resolveAttributes(typeRow, source)),
        ...addCustomBuildYearColumn(t, resolveAttributes(typeRow, source))
      },
      sort: i
    }))

    const columns = typesAsRows.reduce<{ [key: string]: IHeaderItem }>((output, t) => {
      let index = 0
      for (const [key, values] of Object.entries(t.columns)) {
        index++
        if (!values || !values.length) {
          return output
        }

        const existingHeader = output[key]
        if (existingHeader) {
          if (!existingHeader.values.find(v => !!values.find(newV => newV.Value === v))) {
            existingHeader.containsDifferentValues = true
          }
          existingHeader.values = [...existingHeader.values, ...values.map(v => v.Value)]
          output[key] = existingHeader
        } else {
          output[key] = {
            name: values[0].Name,
            displayName: values[0].DisplayName,
            displayType: values[0].DisplayType,
            containsDifferentValues: false,
            values: values.map(v => v.Value),
            sort: columnSortOrder.indexOf(values[0].Name) > -1 ? columnSortOrder.indexOf(values[0].Name) + 1 : 100 + index
          }
        }
      }
      return output
    }, {})

    const generateOptions = (attributeList: string[]) => {
      const options = attributeList.map(v => ({ value: v, label: v }))
      const uniqueOptions = generateUniqueOptions(options)
      const sortedOptions = sortFilterOptions(uniqueOptions)
      return { options: sortedOptions }
    }

    const buildYear = generateBuildYearOptions(typesAsRows, source)
    const headerCells: GridColDef[] = Object.values(columns)
      .filter(
        c =>
          (!!columnSortOrder.find(cs => cs === c.name) || c.containsDifferentValues) &&
          !columnBlacklist.find(cb => cb === c.name) &&
          c.displayType !== 'hidden'
      )
      .sort((a, b) => a.sort - b.sort)
      .map((c): GridColDef => {
        const { options } = generateOptions(c.values)
        return {
          flex: 1,
          field: c.name,
          headerName: c.displayName,
          align: 'left',
          type: 'singleSelect',
          disableColumnMenu: true,
          filterOperators: getGridSingleSelectOperators()
            .filter(op => op.value === 'is')
            .map(operator => ({
              ...operator,
              getApplyFilterFn: filterItem => {
                return ({ value }): boolean => {
                  if (value?.includes(', ')) {
                    return value.includes(filterItem.value)
                  }
                  return value === filterItem.value
                }
              },
              InputComponent: GridHeaderFilterSingleSelect,
              InputComponentProps: { options }
            }))
        }
      })
      .map(header => {
        if (header.field === 'BuildYear') {
          header.filterOperators = GridDateRangeIncludes({ options: buildYear.options }, 'vpom')
          header.minWidth = 200
          header.valueGetter = (value: GridValueGetterParams<IAttributeKeyValues>) =>
            buildYearDisplayValue(t, getRowDateFrom(value), getRowDateTo(value))
        }
        return header
      })

    return { typesAsRows, headerCells }
  }, [types, t])

  const [filteredTableData, setFilter, filter] = useTypeFilter(typesAsRows)
  const [rowCount, setRowCount] = React.useState(filteredTableData.length)
  const { currentFocus, hasRowFocus } = useArrowScrollHandler(rowCount, 1, (index: number) => onKeyScroll(apiRef, index))

  useOnEnterHandler(hasRowFocus, () => {
    const { model } = getRowByIndex(apiRef, currentFocus)
    onTypeSelect(model.selectionValue as IGenericVehicle)
  })

  React.useEffect(() => {
    apiRef.current.subscribeEvent('stateChange', () => {
      const count = getVisibleRowCount(apiRef.current.state)
      setRowCount(count)
    })
  }, [apiRef])

  const rows = React.useMemo(
    () =>
      filteredTableData.map(row => {
        const keys = Object.keys(row.columns)
        return keys.reduce((result: any, key) => {
          result[key] = row.columns[key]!.map(opt => opt.Value).join(', ')
          result.selectionValue = row.value
          return result
        }, {})
      }),
    [filteredTableData]
  )
  return (
    <Stack spacing={2} data-testid='typeselection-row'>
      <DataGridPro
        autoHeight
        disableColumnFilter
        unstable_headerFilters
        disableMultipleRowSelection
        density='compact'
        localeText={localeText}
        apiRef={apiRef}
        columns={headerCells}
        rows={rows}
        hideFooterSelectedRowCount
        pagination={false}
        disableVirtualization // unmounts rows on testing, not necesary for performance on this specific grid
        getRowId={(row: IGenericVehicle) => [...Object.values(row).filter(i => !!i)].toString()}
        slots={{
          headerFilterMenu: null,
          toolbar: DataGridToolbarWithSearch
        }}
        slotProps={{
          toolbar: { name: 'VPOMTypeSelectorSearchInput', placeholder: t('Vehicle.SearchType'), setFilter, filter }
        }}
        onRowClick={(params: GridRowParams<any>) => {
          onTypeSelect(params.row.selectionValue)
        }}
        sx={gridHeaderStyling}
      />
    </Stack>
  )
}
